import { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Tab, Tabs, Badge, Table, Row, Col } from 'react-bootstrap';

//components
import withLocalization from '../../../hoc/withLocalization';
import Button from '~/components/CustomButton/CustomButton.jsx';
import EditNotificationModal from './EditNotifications/EditNotificationModal';

//utils
import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS } from '../../../utils/constants';
import { formatKey } from '../../../utils/commonMethods';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';

//icons
import Edit from '~/assets/img/editing.svg';

const PRIORITY_VARIANT = { HIGH: 'danger', MEDIUM: 'warning', LOW: 'success' };

const NotificationManagement = ({ t, notificationStore }) => {
    const { masterNotifications, loading, lastInteractedId } = notificationStore;
    const [selectedItem, setSelectedItem] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [activeCategory, setActiveCategory] = useState(NOTIFICATION_CATEGORIES[0]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        notificationStore.loadMasterNotifications(true);
        return () => notificationStore.clearLastInteractedId();
    }, []);

    const handleEdit = (item) => {
        notificationStore.setLastInteractedId(item.id);
        setSelectedItem(item);
        setIsCreateMode(false);
        setShowEditModal(true);
    };

    const handleAddNew = () => {
        setIsCreateMode(true);
        setSelectedItem({});
        setShowEditModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedItem(null);
        setIsCreateMode(false);
    };

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setSearch('');
    };

    const matchesSearch = (item) => {
        if (!search.trim()) return true;
        const term = search.trim().toLowerCase();
        return formatKey(item.key).toLowerCase().includes(term) || (item.description || '').toLowerCase().includes(term);
    };

    if (loading) return <LoadingSpinner />;

    return (
        <>
            <Row className="mt-1 mb-2 align-items-center">
                <Col md={8}>
                    <div className="d-flex conversation-search">
                        <input
                            type="text"
                            className="conversation-search-input"
                            placeholder={t('Search notifications')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <i className="conversation-search-icon fa fa-search" />
                    </div>
                </Col>
                <Col md={4} className="text-end">
                    <Button fill onClick={handleAddNew}>{t('Add')} {t('Notification')}</Button>
                </Col>
            </Row>
            <Tabs activeKey={activeCategory} onSelect={handleCategoryChange} id="NotificationCategoryTabs" className="mt-1">
                {NOTIFICATION_CATEGORIES.map(category => (
                    <Tab key={category} eventKey={category} title={t(NOTIFICATION_CATEGORY_LABELS[category])}>
                        <Table hover responsive className="mt-3">
                            <thead>
                                <tr>
                                    <th className="text-start">{t('Notification')}</th>
                                    <th className="text-start">{t('Description')}</th>
                                    <th className="text-center">{t('Push')}</th>
                                    <th className="text-center">{t('Email')}</th>
                                    <th className="text-center">{t('System')}</th>
                                    <th className="text-center">{t('Priority')}</th>
                                    <th className="text-center">{t('Customizable')}</th>
                                    <th className="text-center">{t('Active')}</th>
                                    <th className="text-center">{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(masterNotifications[category] || []).filter(matchesSearch).map(item => (
                                    <tr key={item.id} className={item.id === lastInteractedId ? 'interacted-row' : ''}>
                                        <td className="text-start">{formatKey(item.key)}</td>
                                        <td className="text-start"><small className="text-muted">{item.description || '—'}</small></td>
                                        <td className="text-center">
                                            {item.delivery_options?.allow_push ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />}
                                        </td>
                                        <td className="text-center">
                                            {item.delivery_options?.allow_email ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />}
                                        </td>
                                        <td className="text-center">
                                            {item.delivery_options?.allow_system ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />}
                                        </td>
                                        <td className="text-center">
                                            <Badge bg={PRIORITY_VARIANT[item.priority]}>
                                                {t(item.priority)}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            {item.is_message_customizable ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-muted" />}
                                        </td>
                                        <td className="text-center">
                                            <Badge bg={item.is_active ? 'success' : 'secondary'}>
                                                {item.is_active ? t('Active') : t('Inactive')}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            <Button icon_sm fill onClick={() => handleEdit(item)}>
                                                <img src={Edit} alt="edit" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Tab>
                ))}
            </Tabs>
            <EditNotificationModal
                show={showEditModal}
                onHide={handleCloseModal}
                notification={selectedItem}
                isCreate={isCreateMode}
                onCreated={setActiveCategory}
            />
        </>
    );
};

export default inject('notificationStore')(withLocalization(observer(NotificationManagement)));
